import React from 'react';
import { spacing } from '../../../style';
import TextareaAutogrowInput, { TextAreaAutogrow } from './textarea-autogrow-input';
import TextareaAutogrowContainerComponent from './textarea-autogrow-container';
import { TextareaAutogrowContext } from './textarea-autogrow-context';

export interface ContainerProps {
  className?: string;
  children?: React.ReactNode;
  paddingRight?: keyof typeof spacing;
  paddingLeft?: keyof typeof spacing;
  maxHeight?: string;
}

export interface ButtonContainerProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * TextareaAutogrow - A compound component for auto-growing textareas with optional max height and action buttons.
 *
 * This component automatically adjusts its height based on content, supports maximum height constraints
 * with scrolling, and provides proper spacing for action buttons positioned within the textarea.
 *
 * @example
 * ```tsx
 * <TextareaAutogrow.Container maxHeight="150px" paddingRight="3xxl">
 *   <TextareaAutogrow.TextArea
 *     value={value}
 *     onChange={e => setValue(e.target.value)}
 *     placeholder="Type here..."
 *     size="xl"
 *   />
 *   <TextareaAutogrow.ButtonContainer>
 *     <Button onClick={handleSubmit}>Send</Button>
 *   </TextareaAutogrow.ButtonContainer>
 * </TextareaAutogrow.Container>
 * ```
 *
 * @namespace TextareaAutogrow
 */
const TextareaAutogrow: React.FunctionComponent & {
  /** Container component that provides context and handles max height, padding, and button positioning */
  Container: typeof TextareaAutogrowContainerComponent;
  /** Auto-growing textarea component that adjusts height based on content */
  TextArea: typeof TextareaAutogrowInput;
  /** Container for action buttons positioned within the textarea */
  ButtonContainer: typeof TextareaAutogrowContainerComponent.ButtonContainer;
  /** React context for advanced usage and custom integrations */
  Context: typeof TextareaAutogrowContext;
} = (): React.JSX.Element => {
  throw new Error('TextareaAutogrow is a namespace component. Use TextareaAutogrow.Container instead.');
};

// Attach subcomponents
TextareaAutogrow.Container = TextareaAutogrowContainerComponent;
TextareaAutogrow.TextArea = TextareaAutogrowInput;
TextareaAutogrow.ButtonContainer = TextareaAutogrowContainerComponent.ButtonContainer;
TextareaAutogrow.Context = TextareaAutogrowContext;

// Add display names for better debugging
TextareaAutogrow.displayName = 'TextareaAutogrow';
TextareaAutogrow.Container.displayName = 'TextareaAutogrow.Container';
TextareaAutogrow.TextArea.displayName = 'TextareaAutogrow.TextArea';
TextareaAutogrow.ButtonContainer.displayName = 'TextareaAutogrow.ButtonContainer';

export { TextareaAutogrowInput, TextareaAutogrowContainerComponent };
export type { TextAreaAutogrow };
export default TextareaAutogrow;
